import { useMemo, useState } from 'react';
import {
  Search, Radar, MapPin, History, CalendarDays, Wallet, Sparkles, Users,
  Building2, TrendingUp, Percent, Store, Info, Target,
} from 'lucide-react';
import { usePost } from '@/lib/useData';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { ErrorState } from '@/components/common/ErrorState';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

const EXAMPLES = ['Monoprix Vaugirard', 'Auchan Massy', 'Royan', 'Carrefour Italie 2'];

const scoreColor = (s) => (s >= 80 ? '#0E9F6E' : s >= 65 ? '#0B7A75' : s >= 50 ? '#F4A13A' : '#D64545');
const fmtDate = (d) => (d ? String(d).split('-').reverse().join('/') : '—');

// --- Score gauge -------------------------------------------------------------
function ScoreGauge({ score, label }) {
  const col = scoreColor(score ?? 0);
  const r = 34;
  const C = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score ?? 0));
  const dash = (pct / 100) * C;
  return (
    <div className="flex items-center gap-4">
      <div className="relative h-24 w-24 shrink-0">
        <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90">
          <circle cx="40" cy="40" r={r} fill="none" strokeWidth="8" className="stroke-muted" />
          <circle cx="40" cy="40" r={r} fill="none" stroke={col} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${dash} ${C}`} style={{ transition: 'stroke-dasharray 400ms ease' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-2xl font-bold tabular" style={{ color: col }} data-testid="spot-score">
            {score != null ? Math.round(score) : '—'}
          </span>
          <span className="text-[10px] text-muted-foreground">/ 100</span>
        </div>
      </div>
      <div className="min-w-0">
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold text-white"
          style={{ background: col }} data-testid="spot-verdict">
          <Target className="h-4 w-4" /> {label}
        </span>
        <p className="mt-1.5 text-xs text-muted-foreground break-words">
          Verdict combinant performance historique et potentiel de la zone.
        </p>
      </div>
    </div>
  );
}

const SubBar = ({ label, value, icon: Icon, testid }) => (
  <div data-testid={testid}>
    <div className="mb-1 flex items-center justify-between text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
      <span className="font-semibold tabular text-foreground">{fmtInt(value)}</span>
    </div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: 'var(--tenant-accent-500)' }} />
    </div>
  </div>
);

const Kpi = ({ label, value, icon: Icon, testid }) => (
  <div className="rounded-xl border bg-card p-2.5" data-testid={testid}>
    <p className="flex items-center gap-1 text-[11px] text-muted-foreground break-words">
      <Icon className="h-3.5 w-3.5" /> {label}
    </p>
    <p className="mt-0.5 font-display text-base font-semibold tabular text-foreground break-words">{value}</p>
  </div>
);

const RawStat = ({ label, value }) => (
  <div className="rounded-lg bg-muted/50 p-2">
    <p className="text-[10px] text-muted-foreground break-words">{label}</p>
    <p className="mt-0.5 text-sm font-semibold tabular text-foreground break-words">{value}</p>
  </div>
);

function BestDays({ days, fromArea }) {
  if (!days || !days.length) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border bg-muted/30 p-3" data-testid="spot-best-days">
      <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <CalendarDays className="h-4 w-4 accent-text" /> Meilleurs jours{fromArea ? ' (dans la zone)' : ''} :
      </span>
      {days.map((d) => (
        <span key={d} className="accent-solid rounded-full px-2.5 py-0.5 text-xs font-semibold"
          data-testid="best-day-chip">{d}</span>
      ))}
    </div>
  );
}

// --- Recruiters who worked this spot -----------------------------------------
function SpotRecruiters({ recruiters }) {
  const [expanded, setExpanded] = useState(false);
  if (!recruiters || !recruiters.length) return null;
  const shown = expanded ? recruiters : recruiters.slice(0, 10);
  return (
    <SectionCard
      title="Recruteurs sur ce spot"
      subtitle={`${fmtInt(recruiters.length)} recruteur(s) déjà passé(s) ici · classés par BS validés — repérez qui programmer et sur quels jours`}
      testid="spot-recruiters"
      bodyClassName="!px-0"
    >
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10">#</TableHead>
              <TableHead>Recruteur</TableHead>
              <TableHead className="text-right">BS validés</TableHead>
              <TableHead className="text-right">BS / jour</TableHead>
              <TableHead className="text-right">Transfo.</TableHead>
              <TableHead className="text-right">Don moyen</TableHead>
              <TableHead className="text-right">Âge moy.</TableHead>
              <TableHead className="text-right">Jours</TableHead>
              <TableHead>Meilleurs jours ici</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((r) => (
              <TableRow key={r.recruteur} data-testid="spot-recruiter-row">
                <TableCell className="text-xs font-semibold tabular text-muted-foreground">{r.rank}</TableCell>
                <TableCell>
                  <p className="break-words font-medium text-foreground" data-testid="spot-recruiter-name">{r.recruteur}</p>
                  {r.association_names?.length ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {r.association_names.map((a) => (
                        <Badge key={a} variant="secondary" className="rounded-full px-1.5 py-0 text-[10px] font-normal">{a}</Badge>
                      ))}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="text-right font-semibold tabular text-foreground">{fmtInt(r.bs_reel)}</TableCell>
                <TableCell className="text-right tabular">{fmtNum(r.bs_par_jour, 2)}</TableCell>
                <TableCell className="text-right tabular">{fmtPct(r.taux_transfo)}</TableCell>
                <TableCell className="text-right tabular">{fmtEur(r.don_moyen)}</TableCell>
                <TableCell className="text-right tabular">{r.age_moyen ? `${fmtNum(r.age_moyen, 1)}` : '—'}</TableCell>
                <TableCell className="text-right tabular">{fmtInt(r.jours)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(r.best_days || []).length ? r.best_days.map((d) => (
                      <span key={d} className="accent-solid rounded-full px-2 py-0.5 text-[11px] font-semibold" data-testid="spot-recruiter-bestday">{d}</span>
                    )) : <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {recruiters.length > 10 && (
        <div className="px-4 pt-3">
          <Button variant="outline" size="sm" onClick={() => setExpanded((e) => !e)} data-testid="spot-recruiters-toggle">
            {expanded ? 'Réduire la liste' : `Voir les ${fmtInt(recruiters.length)} recruteurs`}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

// --- Result card -------------------------------------------------------------
function SpotResult({ data }) {
  const v = data.verdict || {};
  const s = data.socio;
  const h = data.history;
  const area = data.area;
  const resolved = data.resolved;

  if (!data.visited && !s) {
    return (
      <SectionCard testid="spot-empty" title="Aucune donnée">
        <div className="flex items-start gap-2 p-1 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
          <span className="break-words">
            Aucune donnée trouvée pour « {data.query} ». Vérifiez l'orthographe ou essayez le nom de la ville la plus proche.
          </span>
        </div>
      </SectionCard>
    );
  }

  const bestDays = (h?.best_days?.length ? h.best_days : area?.best_days) || [];
  const fromArea = !(h?.best_days?.length) && !!(area?.best_days?.length);

  return (
    <div className="space-y-4">
      <SectionCard title={`Verdict — ${data.query}`} testid="spot-result">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <ScoreGauge score={v.score} label={v.label} />
          <div className="flex flex-wrap items-center gap-2">
            {resolved && (
              <Badge variant="secondary" className="gap-1" data-testid="spot-commune">
                <MapPin className="h-3.5 w-3.5" /> {resolved.commune}{resolved.dept ? ` (${resolved.dept})` : ''}
              </Badge>
            )}
            {resolved?.estimated && (
              <Badge variant="outline" className="gap-1" data-testid="spot-estimated-badge">Zone estimée</Badge>
            )}
            <Badge variant={data.visited ? 'secondary' : 'outline'} className="gap-1" data-testid="spot-visited-badge">
              <History className="h-3.5 w-3.5" />
              {data.visited ? `Déjà travaillé · ${fmtInt(h.nb_days)} j` : 'Jamais travaillé'}
            </Badge>
          </div>
        </div>
        <BestDays days={bestDays} fromArea={fromArea} />
      </SectionCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* History */}
        <SectionCard
          title="Historique"
          subtitle={data.visited ? `${fmtInt(h.match_count)} spot(s) correspondant(s) · dernière venue ${fmtDate(h.last_date)}` : 'Aucune venue enregistrée'}
          testid="spot-history"
        >
          {data.visited ? (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Kpi label="BS brut / jour" value={fmtNum(h.avg_bs_rue, 1)} icon={TrendingUp} testid="spot-kpi-bsjour" />
                <Kpi label="Transformation" value={fmtPct(h.taux_transfo)} icon={Percent} testid="spot-kpi-transfo" />
                <Kpi label="Don moyen" value={fmtEur(h.don_moyen)} icon={Wallet} testid="spot-kpi-don" />
                <Kpi label="Âge moyen" value={`${fmtNum(h.age_moyen, 1)} ans`} icon={Users} testid="spot-kpi-age" />
                <Kpi label="Jours travaillés" value={fmtInt(h.nb_days)} icon={CalendarDays} testid="spot-kpi-jours" />
                <Kpi label="BS validés" value={fmtInt(h.total_bs_reel)} icon={Store} testid="spot-kpi-bs" />
              </div>

              <div className="mt-3">
                <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Par canal</p>
                <div className="space-y-1.5">
                  {h.by_canal.map((c) => (
                    <div key={c.canal} className="flex items-start justify-between gap-2 rounded-lg border bg-card px-2.5 py-1.5 text-xs" data-testid="spot-canal-row">
                      <span className="break-words font-medium text-foreground">{c.canal}</span>
                      <span className="shrink-0 text-right text-muted-foreground">
                        {fmtNum(c.avg_bs_rue, 1)} BS brut/j · {fmtInt(c.bs_reel)} validés · {fmtInt(c.days)} j
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-3 text-[11px] text-muted-foreground break-words">
                Performance vs tous les spots travaillés : <strong className="text-foreground">{Math.round(h.perf_score)}/100</strong>
              </p>
            </>
          ) : (
            <div className="space-y-2">
              <div className="rounded-xl border border-dashed bg-muted/30 p-3 text-sm text-muted-foreground break-words">
                Ce spot n'a jamais été travaillé dans nos données.
                {area && area.nb_spots > 0 && (
                  <> Dans <strong className="text-foreground">{resolved?.commune}</strong>, {fmtInt(area.nb_spots)} spot(s) déjà travaillé(s) — {fmtInt(area.total_bs_reel)} BS validés.</>
                )}
              </div>
              {area?.examples?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-muted-foreground">Spots de la même commune</p>
                  {area.examples.map((e) => (
                    <div key={e.lieu} className="flex items-start justify-between gap-2 rounded-lg border bg-card px-2.5 py-1.5 text-xs" data-testid="spot-area-row">
                      <span className="break-words font-medium text-foreground">{e.lieu}</span>
                      <span className="shrink-0 text-muted-foreground">{fmtNum(e.avg_bs_rue, 1)} BS/j · {fmtPct(e.taux_transfo)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SectionCard>

        {/* Zone potential */}
        <SectionCard
          title="Potentiel de la zone"
          subtitle={s ? `${s.commune} (${s.dept})${s.estimated ? ' · estimé' : ''} · score ${fmtNum(s.score, 0)}/100` : 'Commune non identifiée'}
          testid="spot-socio"
        >
          {s ? (
            <>
              <div className="space-y-2.5">
                <SubBar label="Revenu médian" value={s.sub.revenu} icon={Wallet} testid="socio-sub-revenu" />
                <SubBar label="Générosité fiscale" value={s.sub.generosite} icon={Sparkles} testid="socio-sub-generosite" />
                <SubBar label="Âge favorable" value={s.sub.age} icon={Users} testid="socio-sub-age" />
                <SubBar label="Population" value={s.sub.population} icon={Building2} testid="socio-sub-population" />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <RawStat label="Revenu méd." value={s.income ? fmtEur(s.income, 0) : '—'} />
                <RawStat label="Âge médian" value={s.age_med ? `${fmtNum(s.age_med, 0)} ans` : '—'} />
                <RawStat label="Population" value={s.pop ? fmtInt(s.pop) : '—'} />
              </div>
              {s.estimated && (
                <p className="mt-2 text-[11px] text-muted-foreground break-words">
                  Profil estimé d'après les communes voisines (donnée officielle non couverte pour cette commune).
                </p>
              )}
            </>
          ) : (
            <div className="rounded-xl border border-dashed bg-muted/30 p-3 text-sm text-muted-foreground break-words">
              Impossible de rattacher ce spot à une commune. Essayez le nom de la ville la plus proche.
            </div>
          )}
        </SectionCard>
      </div>

      {data.visited && h?.recruiters?.length > 0 && <SpotRecruiters recruiters={h.recruiters} />}
    </div>
  );
}

const InitialPrompt = () => (
  <SectionCard testid="spot-initial">
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl accent-bg-100">
        <Radar className="h-7 w-7 accent-text" />
      </div>
      <p className="font-display text-base font-semibold text-foreground">Analysez n'importe quel spot en un instant</p>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        Tapez une enseigne, un centre commercial ou une ville. Vous obtenez un verdict qui combine l'historique
        (BS/jour, transfo, meilleurs jours…) et le potentiel de la zone (revenu, générosité, âge, population).
      </p>
    </div>
  </SectionCard>
);

// --- Main --------------------------------------------------------------------
export const SpotScanner = ({ body }) => {
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');

  const reqBody = useMemo(() => ({ ...body, query: submitted }), [body, submitted]);
  const enabled = submitted.trim().length >= 3;
  const { data, isLoading, isError, refetch } = usePost('/admin/geo/spot-scan', reqBody, enabled);

  const run = (val) => {
    const value = (val ?? q).trim();
    setQ(value);
    setSubmitted(value);
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title="Analyser un spot"
        subtitle="Tapez une enseigne, un centre commercial ou une ville pour obtenir un verdict instantané"
        testid="spot-scanner"
      >
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
              placeholder="ex. Monoprix Vaugirard, Auchan Massy, Royan…"
              className="pl-9"
              data-testid="spot-search-input"
            />
          </div>
          <Button onClick={() => run()} disabled={q.trim().length < 3} className="gap-1.5" data-testid="spot-search-btn">
            <Radar className="h-4 w-4" /> Analyser
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Exemples :</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => run(ex)}
              className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors duration-150 hover:border-[color:var(--tenant-accent-500)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)]"
              data-testid="spot-example-chip"
            >
              {ex}
            </button>
          ))}
        </div>
      </SectionCard>

      {!enabled ? (
        <InitialPrompt />
      ) : isLoading ? (
        <CardSkeleton height="h-[360px]" />
      ) : isError ? (
        <ErrorState body="Impossible d'analyser ce spot." onRetry={refetch} />
      ) : data?.error ? (
        <SectionCard testid="spot-empty" title="Requête trop courte">
          <p className="text-sm text-muted-foreground">Tapez au moins 3 caractères.</p>
        </SectionCard>
      ) : (
        <SpotResult data={data} />
      )}
    </div>
  );
};
